import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { track } from "@/lib/api";
import {
  ArrowLeft,
  HandsPraying,
  Heart,
  Compass,
  Cross,
  ShieldCheck,
  Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = { HandsPraying, Heart, Compass, Cross, ShieldCheck, Sparkle };

export default function GuidedPrayerPage() {
  const { journeyId } = useParams();
  const navigate = useNavigate();

  const [journeys, setJourneys] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [journey, setJourney] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!journeyId) {
      api.get("/prayer/personal").then((r) => setPersonal(r.data.items || [])).catch(() => {});
    }
  }, [journeyId]);

  useEffect(() => {
    if (journeyId) {
      setLoading(true);
      api.get(`/prayer-journeys/${journeyId}`)
        .then((res) => {
          setJourney(res.data);
          setCurrentStep(res.data.current_step || 0);
          track("guided_prayer_started", { slug: journeyId, name: res.data.name });
        })
        .catch(() => toast.error("Journey not found"))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      api.get("/prayer-journeys")
        .then((res) => setJourneys(res.data.journeys || []))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [journeyId]);

  const saveProgress = async (step, completed = false) => {
    if (!journey) return;
    try {
      await api.post(`/prayer-journeys/${journey.slug}/progress`, {
        current_step: step,
        completed,
      });
    } catch {
      // silent save
    }
  };

  // Picker view
  if (!journeyId) {
    return (
      <div className="px-5 md:px-8 py-4 max-w-lg mx-auto" data-testid="prayer-picker">
        <Link to="/home" className="inline-flex items-center gap-1 text-[#8B7355] text-sm font-body mb-4">
          <ArrowLeft size={16} /> Back
        </Link>
        <h1 className="font-heading text-3xl text-[#2D1B0E] font-medium mb-2 text-center">Guided Prayer</h1>
        <p className="text-[#8B7355] font-body text-sm text-center mb-6">Choose a journey — or let Voice craft one for what's on your heart.</p>

        {/* Personalized CTA */}
        <Link
          to="/guided-prayer/new"
          onClick={() => track("guided_prayer_personalize_clicked", {})}
          data-testid="personalize-cta"
          className="block mb-6 group"
        >
          <div className="glass-card-strong p-5 relative overflow-hidden transition-all group-hover:-translate-y-0.5">
            <div className="spiritual-orb-sm w-32 h-32 -top-10 -right-8 animate-orb opacity-50" />
            <div className="relative z-10 grid grid-cols-[1fr_auto] items-center gap-3">
              <div>
                <p className="text-[#C4A265] text-[10px] uppercase tracking-[0.25em] font-body font-medium mb-1.5 inline-flex items-center gap-1.5">
                  <Sparkle size={10} weight="fill" /> Personalized
                </p>
                <h3 className="font-heading text-lg text-[#2D1B0E] font-medium leading-snug mb-1">
                  Tell Voice what you're carrying.
                </h3>
                <p className="font-body text-xs text-[#8B7355] leading-relaxed">
                  A 5–7 step Scripture-grounded prayer, crafted for your moment.
                </p>
              </div>
              <span className="inline-flex items-center gap-1 text-[#C4A265] font-body text-xs font-medium">
                Begin <ArrowLeft size={12} className="rotate-180" />
              </span>
            </div>
          </div>
        </Link>

        {/* Saved personalized journeys */}
        {personal.length > 0 && (
          <section className="mb-6" data-testid="saved-journeys">
            <p className="text-[#8B7355] text-[10px] uppercase tracking-wider font-body mb-2">Your saved journeys</p>
            <div className="space-y-2">
              {personal.slice(0, 5).map((p) => (
                <Link
                  key={p.id}
                  to={`/guided-prayer/personal/${p.id}`}
                  data-testid={`saved-${p.id}`}
                  className="block glass-card px-4 py-3 transition-all hover:-translate-y-0.5"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-body text-sm font-medium text-[#2D1B0E] truncate">{p.title}</p>
                      <p className="text-[10px] text-[#8B7355] font-body">
                        {p.completed ? "Complete" : `${p.duration_min || 5} min · saved ${new Date(p.created_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <span className="text-[#C4A265] text-xs">{p.completed ? "↻" : "→"}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <p className="text-[#8B7355] text-[10px] uppercase tracking-wider font-body mb-2">Or choose a track</p>
        {loading ? (
          <div className="glass-card p-6 text-center text-[#8B7355] text-sm font-body">Loading journeys…</div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {journeys.map((j) => {
              const Icon = ICONS[j.icon] || HandsPraying;
              const total = j.steps?.length || 6;
              const step = j.current_step || 0;
              return (
                <Link key={j.id} to={`/guided-prayer/${j.slug}`} data-testid={`journey-${j.slug}`}>
                  <div className={`glass-card p-5 text-center ${j.color} transition-all hover:-translate-y-0.5`}>
                    <Icon size={28} weight="duotone" className="text-[#5C4033] mx-auto mb-2" />
                    <p className="font-body text-sm font-medium text-[#2D1B0E]">{j.name}</p>
                    {(step > 0 || j.completed) && (
                      <p className="font-body text-[10px] text-[#C4A265] mt-1">
                        {j.completed ? "Complete" : `Step ${step + 1} of ${total}`}
                      </p>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (loading || !journey) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#8B7355] font-body text-sm">
        Loading…
      </div>
    );
  }

  const steps = journey.steps || [];
  const step = steps[currentStep];
  const total = steps.length;
  const JIcon = ICONS[journey.icon] || HandsPraying;

  const goNext = () => {
    const next = currentStep + 1;
    setCurrentStep(next);
    saveProgress(next);
  };
  const goBack = () => {
    const prev = Math.max(0, currentStep - 1);
    setCurrentStep(prev);
    saveProgress(prev);
  };
  const finish = async () => {
    await saveProgress(total - 1, true);
    track("guided_prayer_completed", { slug: journey.slug });
    toast.success("Journey completed. Amen.");
    navigate("/home");
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-8 relative" data-testid="guided-prayer">
      <div className="spiritual-orb-connect w-72 h-72 top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      <div className="relative z-10 w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-2 mb-2 text-[#C4A265]">
          <JIcon size={16} weight="duotone" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-body font-medium">{journey.name}</span>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl text-[#2D1B0E] mb-4">Guided Prayer</h1>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-2">
          {steps.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === currentStep ? "bg-[#C4A265] w-3 h-3" : i < currentStep ? "bg-[#C4A265]/50" : "bg-[#D4C9BA]"}`} />
          ))}
        </div>
        <p className="text-[#8B7355] font-body text-sm mb-10">{currentStep + 1}/{total}</p>

        {/* Orb container */}
        <div className="relative mb-8">
          <div className="w-48 h-48 mx-auto rounded-full bg-gradient-to-br from-[#F0E4D4]/60 via-[#EDE6DC]/40 to-transparent flex items-center justify-center">
            <div className="text-center">
              <p className="font-heading text-2xl text-[#2D1B0E] leading-tight">Step {currentStep + 1} —</p>
              <p className="font-heading text-3xl text-[#2D1B0E] font-medium">{step?.title}</p>
            </div>
          </div>
        </div>

        <p className="font-body text-sm text-[#5C4033] leading-relaxed mb-10 max-w-sm mx-auto italic">
          "{step?.text}"
        </p>

        <div className="flex gap-3 justify-center">
          {currentStep > 0 && (
            <button
              onClick={goBack}
              data-testid="prayer-back-btn"
              className="px-6 py-3 rounded-full glass-card text-[#8B7355] font-body text-sm hover:bg-white/60 transition-all"
            >
              Back
            </button>
          )}
          {currentStep < total - 1 ? (
            <button
              onClick={goNext}
              data-testid="prayer-continue-btn"
              className="px-8 py-3 rounded-full glass-card-strong text-[#5C4033] font-body text-sm font-medium hover:bg-white/80 transition-all"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={finish}
              data-testid="prayer-complete-btn"
              className="px-8 py-3 rounded-full glass-card-strong text-[#5C4033] font-body text-sm font-medium hover:bg-white/80 transition-all"
            >
              Amen — Complete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
